import './Toast.less'

import html from './Helper/html'

const TOASTS_CONTAINER = `<div class="sh-toasts-container"></div>`
const TOAST_WRAPPER = `<div class="sh-toast"></div>`
const TOAST_TITLE = `<div class="sh-toast__title"></div>`
const TOAST_CONTENT = `<div class="sh-toast__content"></div>`
const TOAST_LINK = `<a class="sh-toast__link"></a>`
const TOAST_CLOSE_BUTTON = `<button class="sh-toast__close-button"><img src="/images/component/toast/icon_close.svg" alt="Close icon" /></button>`
const TOAST_TIMEOUT = `<figure><svg viewBox="0 0 120 120"><path/></svg></figure>`

const TOAST_TYPE = {
    WARNING: 'warning',
    SUCCESS: 'success',
    INFO: 'info',
    ERROR: 'error'
}

type ToastOptions = {
    title?: string,
    content: string | Error | HTMLElement | unknown,
    action?: {
        href?: string,
        onClick?: (Event) => void,
        title: string
    } | null,
    timeout?: number,
    dismissable?: boolean
};

type ToastFn = (passedOptions: ToastOptions) => { dismiss: () => void }

export type Toast = {
    success: ToastFn,
    info: ToastFn,
    warning: ToastFn,
    error: ToastFn,
    clearAll: () => void,
}

export default (function (): Toast {
    const _container: HTMLElement = html`${TOASTS_CONTAINER}`

    const _attachWrapper = () => {
        if (!document.body.contains(_container)) { document.body.appendChild(_container) }
    }

    const _setDefaults = ({ action, timeout, dismissable, ...options }: ToastOptions) => { // eslint-disable-line react/function-component-definition
        _attachWrapper()

        return {
            action: action || null,
            timeout: timeout || 0,
            dismissable: typeof dismissable === 'undefined' || dismissable === true,
            ...options
        }
    }

    const _makeTitle = (data) => {
        const toastTitle = html`${TOAST_TITLE}`
        toastTitle.textContent = data
        return toastTitle
    }

    const _makeContent = (data, action) => {
        const toastContent = html`${TOAST_CONTENT}`

        if (data) {
            const text = document.createElement('span')

            if (data instanceof HTMLElement) {
                text.append(data)
            } else {
                text.innerHTML = data
            }
            toastContent.appendChild(text)
        }

        if (action) {
            const link: HTMLElement = html`${TOAST_LINK}`
            link.textContent = action.title

            if (action.href) {
                link.setAttribute('href', action.href)
            }

            if (action.onClick) {
                link.addEventListener('click', action.onClick)
            }

            toastContent.appendChild(link)
        }

        return toastContent
    }

    const _makeToast = (type: string, options: ToastOptions): HTMLElement => {
        const toastElement: HTMLElement = html`${TOAST_WRAPPER}`
        const closeButton = html`${TOAST_CLOSE_BUTTON}`

        if (options.title && options.content) {
            toastElement.appendChild(_makeTitle(options.title))
            toastElement.appendChild(_makeContent(options.content, options.action))
        } else if (options.title) {
            toastElement.appendChild(_makeTitle(options.title))
            toastElement.appendChild(_makeContent(null, options.action))
        } else if (options.content) {
            toastElement.appendChild(_makeTitle(options.content))
        } else {
            // really?
        }

        if (options.dismissable) {
            toastElement.appendChild(closeButton)

            closeButton.addEventListener('click', e => {
                const target = e.target as HTMLInputElement
                _destroyToast(target.closest('.sh-toast')!)
            })
        }

        _container.insertBefore(toastElement, _container.firstChild)
        toastElement.classList.add(`__${type}`, '__animation')

        if (options.timeout! > 0) {
            const timeoutSpinner = html`${TOAST_TIMEOUT}`
            toastElement.appendChild(timeoutSpinner)

            _runTimeoutAnimation(timeoutSpinner, options.timeout! * 1000)

            setTimeout(() => {
                _destroyToast(toastElement)
            }, options.timeout! * 1000)
        }

        return toastElement
    }

    const _destroyToast = (toast: HTMLElement) => {
        toast.remove()
    }

    const _runTimeoutAnimation = (timeoutSpinner: HTMLElement, time: number) => {
        let count = 360

        const path = timeoutSpinner.querySelector('path')!

        const anim = () => {
            count--

            if (count !== 0) {
                path.setAttribute('d', arc(60, 60, 50, 0, count))
            } else {
                clearTimeout(running)
                count = 360
            }
        }

        const running = setInterval(function () { anim() }, time / count)
    }

    const success = (passedOptions: ToastOptions) => {
        const toast = _makeToast(TOAST_TYPE.SUCCESS, _setDefaults(passedOptions))
        return { dismiss: () => { _destroyToast(toast) } }
    }

    const warning = (passedOptions: ToastOptions) => {
        const toast = _makeToast(TOAST_TYPE.WARNING, _setDefaults(passedOptions))
        return { dismiss: () => { _destroyToast(toast) } }
    }

    const info = (passedOptions: ToastOptions) => {
        const toast = _makeToast(TOAST_TYPE.INFO, _setDefaults(passedOptions))
        return { dismiss: () => { _destroyToast(toast) } }
    }

    const error = (passedOptions: ToastOptions) => {
        const toast = _makeToast(TOAST_TYPE.ERROR, _setDefaults(passedOptions))
        return { dismiss: () => { _destroyToast(toast) } }
    }

    const clearAll = () => {
        _container.innerHTML = ''
    }

    return {
        success,
        warning,
        info,
        error,
        clearAll,
    }
}())

// https://codepen.io/depthdev/pen/bNyxzG
function arc (x: number, y: number, r: number, s: number, f: number, rotate?: number) {
    // Parameters: x-axis center, y-axis center, radius, start degrees, finish degrees, rotation in degrees (optional)
    function coords (degrees) {
        const radians = (degrees - (90 - (rotate || 0))) * Math.PI / 180.0
        return {
            x: x + (r * Math.cos(radians)),
            y: y + (r * Math.sin(radians))
        }
    }

    const start = coords(s)
    const finish = coords(f)

    const d = [
        'M', start.x, start.y,
        'A', r, r, 0, ~~(f - s > 180), 1, finish.x, finish.y
    ].join(' ')

    return d
} // end arc()
